import React from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { NOOP } from '../../../utils/const';
import Button from '../Button/Button';
import DownIcon from '../../Icons/DownIcon';
import UpIcon from '../../Icons/UpIcon';
import DropdownList from '../DropdownList/DropdownList';
import useMenuOpenState from '../Hooks/useMenuOpenState';
import useChildrenWithText from '../Hooks/useChildrenWithText';

const cssBlock = 'v2-split-button';

/**
Bottone che offre le stesse possibilità di un `Button`,
ma permette anche di scegliere un'azione che appartiene alla stessa area semantica di quella principale partendo da un set di alternative.

L'elemento `SplitButton.DropdownList`, presente nei children, riporta la lista dei bottoni che verranno aggiunti al DropDown.
 */
const SplitButton = (props) => {
  const {
    emphasis, primary,
    running, disabled, onClick,
    children, className,
  } = props;
  const [dropDownItems, buttonChildren] = DropdownList.extractDropDownList(children);
  const childrenWithText = useChildrenWithText(buttonChildren);
  const { open, onOpen, onClose } = useMenuOpenState();

  const classes = cns(
    cssBlock,
    `${cssBlock}--${emphasis.toLowerCase()}`,
    {
      [`${cssBlock}--primary`]: primary,
      [`${cssBlock}--disabled`]: disabled,
    },
    className,
  );

  return (
    <div className={classes}>
      <Button
        blockClassName={`${cssBlock}__button`}
        emphasis={emphasis}
        primary={primary}
        running={running}
        disabled={disabled}
        onClick={onClick}
      >
        {childrenWithText}
      </Button>
      <button
        type="button"
        disabled={disabled}
        className={`${cssBlock}__toggle`}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {open ? <UpIcon /> : <DownIcon />}
      </button>
      {open && dropDownItems}
    </div>
  );
};

SplitButton.Emphasis = Button.Emphasis;

SplitButton.propTypes = {
  /** Enfasi del bottone */
  emphasis: PropTypes.oneOf([SplitButton.Emphasis.LOW, SplitButton.Emphasis.MEDIUM, SplitButton.Emphasis.HIGH]),

  /** Definisce l'azione principale */
  primary: PropTypes.bool,

  /**
Se true, il bottone non genera alcun evento.
  */
  disabled: PropTypes.bool,

  /**
Se true, il bottone visualizza uno spinner ad indicare un processo attivo.
Un bottone in running è sempre disabled.
La gestione della proprietà `running` può essere controlled o uncontrolled.

Gestione controlled: la proprietà assume un valore `true`/`false`.
In questo caso lo stato running è totalmente controllato dalla proprietà ed il componenente non applicherà nessuna logica che lo modifichi.

Gestione uncontrolled: la proprietà assume un valore `undefined`.
In questo caso lo stato running è definito dal componente. Vedi proprtietà `onClick`.
  */
  running: PropTypes.bool,

  /**
Callback evento di click del bottone.
Parametri:

- evento: evento generato dal click.

Ritorno:

- null | undefined | Promise:
se running è uncontrolled e la callback ritorna una Promise, il bottone rimane in stato running fino alla sua conclusione.
  */
  onClick: PropTypes.func,

  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /** Children del componente. I children di tipo `SplitButton.DropdownList` faranno parte del menù di drop down */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

SplitButton.defaultProps = {
  emphasis: SplitButton.Emphasis.LOW,
  primary: false,
  disabled: false,
  running: undefined,
  onClick: NOOP,
  children: undefined,
  className: undefined,
};

SplitButton.DropdownList = DropdownList;

export default SplitButton;
